import React, { useState, useEffect, useRef } from 'react';
import { Flex, Box } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import {
  Heading, Row, Section, Loading, Title, Subtitle, Link,
} from '../components';
import api from '../api';

const LINK_TO_DOCS = 'https://developers.liveperson.com/intent-builder-overview.html';

function IntentPage(props) {
  const {
    name, addNotification, brandId, themeMode,
  } = props;

  const isMounted = useRef(true);

  const [intents, setIntents] = useState([]);
  const [isFetchingIntents, setIsFetchingIntents] = useState(null);

  const getIntentData = async () => {
    if (!isFetchingIntents) {
      if (isMounted.current) {
        setIsFetchingIntents(true);
      }
      try {
        const data = await api.account.getIntents(brandId);
        if (isMounted.current) {
          setIntents(data);
        }
      } catch (error) {
        addNotification(error, 'error');
      }
      if (isMounted.current) {
        setIsFetchingIntents(false);
      }
    }
  };

  useEffect(() => {
    async function fetchData() {
      await getIntentData();
    }
    fetchData();
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line

  const subtitle = (
    <Box>
      To learn more about how to setup and manage intents, see the
      {' '}
      <Link href={LINK_TO_DOCS} target="_blank" rel="noopener noreferrer">
        documentation
      </Link>
      .
    </Box>
  );

  const hasIntents = (intents.length > 0);

  return (
    <Box>
      <Heading>{name}</Heading>
      <Subtitle mb={3}>
        This shows you the list of intents that you have setup in Intent Builder.
      </Subtitle>
      <Section title="Intents" subtitle={subtitle} mb={3}>
        {(isFetchingIntents === false) && (
          <Title fontSize={1} mb={2} data-testid="intent-title">
            {hasIntents ? 'Click to copy an intent' : 'You currently have no intents configured. You can setup new intents in Intent Builder.'}
          </Title>
        )}
        {isFetchingIntents !== false ? (
          <Flex align="center" justifyContent="center" py={3}>
            <Loading size={24} />
          </Flex>
        ) : (
          intents.map((intent, index) => (
            <Row
              themeMode={themeMode}
              key={intent.name}
              data={intent}
              rowType="list"
              isLast={index + 1 === intents.length}
              enableCopy
            />
          ))
        )}
      </Section>
    </Box>
  );
}

IntentPage.propTypes = {
  themeMode: PropTypes.string.isRequired,
  addNotification: PropTypes.func.isRequired,
  brandId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

export default IntentPage;
